// src/pages/support/SupportTickets.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Divider,
    Grid
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Reply as ReplyIcon,
    CheckCircle as CheckIcon,
    Assignment as AssignIcon,
    Close as CloseIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext'; // Importar contexto de autenticación

// Componente TabPanel
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const SupportTickets = () => {
    const [tabValue, setTabValue] = useState(0);
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Ticket detalle
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState('');
    const [technicians, setTechnicians] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Obtener información del usuario actual
    const { user: currentUser } = useAuth();

    useEffect(() => {
        fetchTickets();
        fetchTechnicians();
    }, []);

    useEffect(() => {
        if (tickets.length > 0) {
            filterTickets();
        }
    }, [searchTerm, statusFilter, tickets]);

    // Función para verificar si el usuario actual puede editar el ticket
    const canEditTicket = (ticket) => {
        if (!currentUser || !ticket) return false;

        // Los administradores siempre pueden editar
        if (currentUser.role === 'admin') return true;

        // El usuario asignado puede editar
        if (ticket.assigned_to === currentUser.id) return true;

        // En otros casos, no se permite editar
        return false;
    };

    // Función para verificar si el usuario puede asignar tickets
    const canAssignTicket = () => {
        if (!currentUser) return false;

        // Solo los administradores pueden asignar tickets
        return currentUser.role === 'admin';
    };

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/support/tickets', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener tickets');
            }

            const data = await response.json();
            setTickets(data);
            setFilteredTickets(data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            setError('Error al cargar los tickets');
        } finally {
            setLoading(false);
        }
    };

    const fetchTechnicians = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/users?role=technician', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setTechnicians(data);
            }
        } catch (error) {
            console.error('Error fetching technicians:', error);
        }
    };

    const filterTickets = () => {
        let filtered = [...tickets];

        // Filtro por estado
        if (statusFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.status === statusFilter);
        }

        // Filtro por búsqueda
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(search) ||
                (ticket.client_name && ticket.client_name.toLowerCase().includes(search)) ||
                ticket.id.toLowerCase().includes(search)
            );
        }

        setFilteredTickets(filtered);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleViewTicket = async (ticketId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/support/tickets/${ticketId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener detalles del ticket');
            }

            const data = await response.json();
            setSelectedTicket(data);
            setViewDialogOpen(true);
        } catch (error) {
            console.error('Error fetching ticket details:', error);
            setError('Error al obtener detalles del ticket');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedTicket(null);
    };

    const handleOpenReplyDialog = () => {
        // Verificar si el usuario puede responder
        if (!canEditTicket(selectedTicket)) {
            setError('No tienes permiso para responder a este ticket');
            return;
        }

        setReplyDialogOpen(true);
        setReplyText('');
    };

    const handleCloseReplyDialog = () => {
        setReplyDialogOpen(false);
    };

    const handleSubmitReply = async () => {
        if (!replyText.trim()) return;

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/support/tickets/${selectedTicket.id}/responses`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    content: replyText,
                    response_type: 'staff'
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al enviar respuesta');
            }

            // Cerrar diálogo y recargar ticket
            handleCloseReplyDialog();
            await handleViewTicket(selectedTicket.id);
            await fetchTickets();
        } catch (error) {
            console.error('Error submitting reply:', error);
            setError(error.message || 'Error al enviar respuesta');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenAssignDialog = () => {
        // Verificar si el usuario puede asignar tickets
        if (!canAssignTicket()) {
            setError('Solo los administradores pueden asignar tickets');
            return;
        }

        setAssignDialogOpen(true);
        setSelectedTechnician(selectedTicket.assigned_to || '');
    };

    const handleCloseAssignDialog = () => {
        setAssignDialogOpen(false);
    };

    const handleAssignTicket = async () => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/support/tickets/${selectedTicket.id}/assign`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    assigned_to: selectedTechnician
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al asignar ticket');
            }

            // Cerrar diálogo y recargar ticket
            handleCloseAssignDialog();
            await handleViewTicket(selectedTicket.id);
            await fetchTickets();
        } catch (error) {
            console.error('Error assigning ticket:', error);
            setError(error.message || 'Error al asignar ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateStatus = async (status) => {
        // Verificar si el usuario puede actualizar el estado
        if (!canEditTicket(selectedTicket)) {
            setError('No tienes permiso para actualizar este ticket');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/support/tickets/${selectedTicket.id}/status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error al cambiar estado a ${status}`);
            }

            // Recargar ticket y lista
            await handleViewTicket(selectedTicket.id);
            await fetchTickets();
        } catch (error) {
            console.error('Error updating status:', error);
            setError(error.message || `Error al cambiar estado a ${status}`);
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'open':
                return <Chip label="Abierto" color="primary" size="small" />;
            case 'closed':
                return <Chip label="Cerrado" color="default" size="small" />;
            case 'in_progress':
                return <Chip label="En Progreso" color="secondary" size="small" />;
            case 'client_response':
                return <Chip label="Respuesta Cliente" color="warning" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getPriorityChip = (priority) => {
        switch (priority) {
            case 'high':
                return <Chip label="Alta" color="error" size="small" />;
            case 'medium':
                return <Chip label="Media" color="warning" size="small" />;
            case 'low':
                return <Chip label="Baja" color="success" size="small" />;
            default:
                return <Chip label={priority} size="small" />;
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 100, valueGetter: (params) => params.row.id.slice(0, 8) },
        {
            field: 'client_name',
            headerName: 'Cliente',
            width: 180,
            valueGetter: (params) => params.row.client_name || 'N/A'
        },
        { field: 'subject', headerName: 'Asunto', width: 250 },
        {
            field: 'created_at',
            headerName: 'Fecha',
            width: 150,
            valueFormatter: (params) => format(new Date(params.value), 'dd/MM/yyyy HH:mm')
        },
        {
            field: 'priority',
            headerName: 'Prioridad',
            width: 120,
            renderCell: (params) => getPriorityChip(params.value)
        },
        {
            field: 'status',
            headerName: 'Estado',
            width: 150,
            renderCell: (params) => getStatusChip(params.value)
        },
        {
            field: 'response_count',
            headerName: 'Respuestas',
            width: 100,
            type: 'number'
        },
        {
            field: 'assigned_to_name',
            headerName: 'Asignado a',
            width: 150,
            valueGetter: (params) => {
                if (params.row.assigned_to_name && params.row.assigned_to_lastname) {
                    return `${params.row.assigned_to_name} ${params.row.assigned_to_lastname}`;
                }
                return 'Sin asignar';
            }
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            renderCell: (params) => (
                <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleViewTicket(params.row.id)}
                >
                    <ViewIcon />
                </IconButton>
            )
        }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Gestión de Tickets de Soporte
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Todos los Tickets" />
                    <Tab label="Sin Asignar" />
                    <Tab label="En Progreso" />
                    <Tab label="Esperando Respuesta" />
                </Tabs>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <TextField
                    placeholder="Buscar tickets..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />

                <TextField
                    select
                    label="Estado"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ width: 200 }}
                >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="open">Abiertos</MenuItem>
                    <MenuItem value="in_progress">En Progreso</MenuItem>
                    <MenuItem value="client_response">Respuesta Cliente</MenuItem>
                    <MenuItem value="closed">Cerrados</MenuItem>
                </TextField>

                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchTickets}
                >
                    Actualizar
                </Button>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <DataGrid
                    rows={filteredTickets}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    autoHeight
                    loading={loading}
                    getRowId={(row) => row.id}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <DataGrid
                    rows={tickets.filter(ticket => !ticket.assigned_to)}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    autoHeight
                    loading={loading}
                    getRowId={(row) => row.id}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <DataGrid
                    rows={tickets.filter(ticket => ticket.status === 'in_progress')}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    autoHeight
                    loading={loading}
                    getRowId={(row) => row.id}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <DataGrid
                    rows={tickets.filter(ticket => ticket.status === 'client_response')}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    autoHeight
                    loading={loading}
                    getRowId={(row) => row.id}
                />
            </TabPanel>

            {/* Diálogo de Visualización de Ticket */}
            <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
                {selectedTicket && (
                    <>
                        <DialogTitle>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">
                                    Ticket: {selectedTicket.subject}
                                </Typography>
                                {getStatusChip(selectedTicket.status)}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">
                                        ID
                                    </Typography>
                                    <Typography variant="body2">
                                        #{selectedTicket.id.slice(0, 8)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">
                                        Cliente
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedTicket.client_name || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">
                                        Fecha
                                    </Typography>
                                    <Typography variant="body2">
                                        {format(new Date(selectedTicket.created_at), 'dd/MM/yyyy HH:mm')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" color="textSecondary">
                                        Prioridad
                                    </Typography>
                                    <Typography variant="body2">
                                        {getPriorityChip(selectedTicket.priority)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Categoría
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedTicket.category || 'General'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Asignado a
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedTicket.assigned_to_name && selectedTicket.assigned_to_lastname
                                            ? `${selectedTicket.assigned_to_name} ${selectedTicket.assigned_to_lastname}`
                                            : 'Sin asignar'}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" gutterBottom>
                                Descripción
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                                <Typography variant="body2">{selectedTicket.description}</Typography>
                            </Paper>

                            <Divider sx={{ mb: 3 }} />

                            <Typography variant="subtitle1" gutterBottom>
                                Historial de Respuestas
                            </Typography>

                            {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                                <Box sx={{ mb: 3 }}>
                                    {selectedTicket.responses.map((response, index) => (
                                        <Paper
                                            key={index}
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                bgcolor: response.response_type === 'staff' ? 'primary.50' : 'success.50'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="subtitle2">
                                                    {response.response_type === 'staff' ? 'Staff: ' + response.response_by : 'Cliente'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {format(new Date(response.created_at), 'dd/MM/yyyy HH:mm')}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2">{response.content}</Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            ) : (
                                <Paper sx={{ p: 2, textAlign: 'center', mb: 3 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        No hay respuestas aún
                                    </Typography>
                                </Paper>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                {selectedTicket.status !== 'closed' && (
                                    <>
                                        {/* Botón de asignación - solo para administradores */}
                                        {canAssignTicket() && (
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<AssignIcon />}
                                                onClick={handleOpenAssignDialog}
                                            >
                                                {selectedTicket.assigned_to ? 'Reasignar' : 'Asignar'}
                                            </Button>
                                        )}

                                        {/* Botones de acción - solo para el asignado o administradores */}
                                        {canEditTicket(selectedTicket) && (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<ReplyIcon />}
                                                    onClick={handleOpenReplyDialog}
                                                >
                                                    Responder
                                                </Button>

                                                {selectedTicket.status !== 'in_progress' && (
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() => handleUpdateStatus('in_progress')}
                                                    >
                                                        Marcar En Progreso
                                                    </Button>
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    startIcon={<CheckIcon />}
                                                    onClick={() => handleUpdateStatus('closed')}
                                                >
                                                    Cerrar Ticket
                                                </Button>
                                            </>
                                        )}
                                    </>
                                )}
                                {selectedTicket.status === 'closed' && canEditTicket(selectedTicket) && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleUpdateStatus('open')}
                                    >
                                        Reabrir Ticket
                                    </Button>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseViewDialog} startIcon={<CloseIcon />}>
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Diálogo de Respuesta */}
            <Dialog open={replyDialogOpen} onClose={handleCloseReplyDialog} maxWidth="md" fullWidth>
                <DialogTitle>Responder al Ticket</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Tu Respuesta"
                        variant="outlined"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseReplyDialog}>Cancelar</Button>
                    <Button
                        onClick={handleSubmitReply}
                        variant="contained"
                        color="primary"
                        disabled={!replyText.trim() || submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Enviar Respuesta'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Asignación */}
            <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog}>
                <DialogTitle>Asignar Ticket</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Asignar a"
                        value={selectedTechnician}
                        onChange={(e) => setSelectedTechnician(e.target.value)}
                        margin="normal"
                    >
                        <MenuItem value="">Sin asignar</MenuItem>
                        {technicians.map((tech) => (
                            <MenuItem key={tech.id} value={tech.id}>
                                {tech.first_name} {tech.last_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAssignDialog}>Cancelar</Button>
                    <Button
                        onClick={handleAssignTicket}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Asignar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupportTickets;